import {
  hasActiveSharePointItem,
  saveActiveFileToSharePoint,
  setActiveSharePointBinding
} from "./graphService";
import { saveHostDocumentNative } from "./officeService";

/**
 * When GET /generated-contracts includes SharePoint ids on `stored_file`, bind them so
 * {@link persistHostedDocumentToSharePointAfterReview} can upload after Approve/Reject.
 */
export function bindSharePointTargetingFromContract(contract: {
  readonly graphDriveId?: string | null;
  readonly graphItemId?: string | null;
}): void {
  const d = contract.graphDriveId?.trim();
  const i = contract.graphItemId?.trim();
  if (d && i) {
    setActiveSharePointBinding(d, i);
  }
}

/** Optional Graph targets from GET /generated-contracts `stored_file` (same as post-commit upload). */
export type SharePointUploadTarget = {
  readonly graphDriveId?: string | null;
  readonly graphItemId?: string | null;
};

/**
 * Uploads the current Word document bytes to the active SharePoint item when one is bound
 * to this session (see `graphService`). No-op when the host file is not SharePoint-backed.
 *
 * Pass `target` when the session may not have been opened via `loadFileIntoWord` but the
 * contract row includes `stored_file.drive_id` / `item_id`.
 */
export async function persistHostedDocumentToSharePointAfterReview(
  target?: SharePointUploadTarget
): Promise<void> {
  if (target?.graphDriveId?.trim() && target?.graphItemId?.trim()) {
    setActiveSharePointBinding(target.graphDriveId, target.graphItemId);
  }
  if (!hasActiveSharePointItem()) {
    return;
  }
  try {
    await saveActiveFileToSharePoint();
  } catch (e) {
    const detail = e instanceof Error ? e.message : String(e);
    throw new Error(`Uploading the document to SharePoint failed. ${detail}`.slice(0, 600), {
      cause: e
    });
  }
}

/**
 * Preferred save path for manual save actions:
 * 1) native host save (Word semantics), 2) Graph upload fallback when native save is unavailable.
 */
export async function saveDocumentPreferred(
  target?: SharePointUploadTarget
): Promise<void> {
  if (target?.graphDriveId?.trim() && target?.graphItemId?.trim()) {
    setActiveSharePointBinding(target.graphDriveId, target.graphItemId);
  }

  const native = await saveHostDocumentNative();
  if (native.ok) {
    return;
  }

  if (native.reason !== "unavailable") {
    throw new Error(native.message);
  }

  if (!hasActiveSharePointItem()) {
    throw new Error(
      "Native save is not available in this Word host, and this contract is not linked to a SharePoint file."
    );
  }

  try {
    await saveActiveFileToSharePoint();
  } catch (e) {
    const detail = e instanceof Error ? e.message : String(e);
    throw new Error(`Saving to SharePoint failed. ${detail}`.slice(0, 600), {
      cause: e
    });
  }
}

/** Manual save: requires SharePoint targets and throws when the contract is not linked. */
export async function syncHostedDocumentToSharePoint(
  target: SharePointUploadTarget
): Promise<void> {
  const driveId = target.graphDriveId?.trim();
  const itemId = target.graphItemId?.trim();
  if (!driveId || !itemId) {
    throw new Error("This contract is not linked to a SharePoint file.");
  }
  await persistHostedDocumentToSharePointAfterReview({
    graphDriveId: driveId,
    graphItemId: itemId,
  });
}
